import { fill, type MyContributionsCopy } from "./my-contributions-copy";
import type { LanguageRow } from "./contribution-history";

interface LanguageBreakdownProps {
  copy: MyContributionsCopy;
  rows: LanguageRow[];
  languageName: (code: string | null) => string;
  format: (value: number) => string;
}

/** Audios per language: what was sent, what was kept, and the rate. */
export function LanguageBreakdown({ copy, rows, languageName, format }: LanguageBreakdownProps) {
  return (
    <section aria-labelledby="mc-languages" className="space-y-3">
      <h2 id="mc-languages" className="text-sm font-medium text-muted-foreground">
        {copy.languagesTitle}
      </h2>
      <div className="overflow-hidden rounded-lg border border-border/60 bg-surface">
        <table className="w-full text-sm">
          <thead className="border-b border-border/60 bg-surface-2 text-left text-xs text-muted-foreground">
            <tr>
              <th scope="col" className="px-4 py-2.5 font-medium">{copy.colLanguage}</th>
              <th scope="col" className="px-4 py-2.5 text-right font-medium">{copy.colSent}</th>
              <th scope="col" className="px-4 py-2.5 text-right font-medium">{copy.colAccepted}</th>
              <th scope="col" className="hidden px-4 py-2.5 text-right font-medium sm:table-cell">{copy.colRejected}</th>
              <th scope="col" className="hidden w-[30%] px-4 py-2.5 font-medium md:table-cell">{copy.colRate}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/60">
            {rows.map((row) => {
              const rateLabel = fill(copy.rateValue, { rate: row.rate });
              return (
                <tr key={row.code ?? "unknown"}>
                  <th scope="row" className="px-4 py-3 text-left font-medium text-foreground">
                    {languageName(row.code)}
                  </th>
                  <td className="px-4 py-3 text-right tabular-nums text-foreground">{format(row.sent)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-success">{format(row.accepted)}</td>
                  <td className="hidden px-4 py-3 text-right tabular-nums text-muted-foreground sm:table-cell">{format(row.rejected)}</td>
                  <td className="hidden px-4 py-3 md:table-cell">
                    <div className="flex items-center gap-3" title={rateLabel}>
                      <div
                        className="h-1.5 flex-1 overflow-hidden rounded-full bg-border/70"
                        role="progressbar"
                        aria-label={rateLabel}
                        aria-valuenow={row.rate}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      >
                        <div className="h-full rounded-full bg-success" style={{ width: `${row.rate}%` }} />
                      </div>
                      <span className="w-10 text-right text-xs tabular-nums text-muted-foreground">{row.rate}&nbsp;%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
